import React, { useState, useMemo } from 'react';
import { Head } from 'vite-react-ssg';
import { Search, Filter, BookOpen } from 'lucide-react';
import { BlogCard } from '../components/blog/BlogCard';
import { BlogPost, BlogCategory } from '../types/blog';
import { getCanonicalUrl } from '../utils/canonical';

// Import blog data synchronously for SSG
// This file is generated by `npm run sync-blog`
import blogIndexData from '../../public/blog-data/index.json';

// Type the imported data
const staticPosts: BlogPost[] = (blogIndexData as any[]).map(post => ({
  ...post,
  readingTime: typeof post.readingTime === 'object'
    ? post.readingTime
    : { text: `${post.readingTime} min read`, minutes: post.readingTime, time: 0, words: 0 }
})).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

export const BlogListSSG: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<BlogCategory | 'all'>('all');

  // Use static data (no async loading needed for SSG)
  const posts = staticPosts;

  // Filter posts based on search and category
  const filteredPosts = useMemo(() => {
    let filtered = posts;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(post => post.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(post =>
        post.title.toLowerCase().includes(term) ||
        post.description.toLowerCase().includes(term) ||
        post.tags.some(tag => tag.toLowerCase().includes(term))
      );
    }

    return filtered;
  }, [posts, searchTerm, selectedCategory]);

  const categories = Object.values(BlogCategory);

  return (
    <>
      <Head>
        <title>Blog - Web Accessibility Insights | Accessibility Checker</title>
        <meta
          name="description"
          content="Discover articles, guides and best practices on web accessibility, WCAG standards and barrier-free web design."
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Web Accessibility Blog" />
        <meta property="og:description" content="Guides, Best Practices and Insights for Accessible Web Development" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={getCanonicalUrl('/blog')} />
      </Head>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-blue-600 to-blue-700 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <BookOpen className="w-16 h-16 mx-auto mb-4" aria-hidden="true" />
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Web Accessibility Blog
              </h1>
              <p className="text-xl text-blue-100">
                Guides, Best Practices and Insights for Accessible Web Development
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        <section className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <label htmlFor="search" className="sr-only">
                  Search Blog
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" aria-hidden="true" />
                  <input
                    id="search"
                    type="search"
                    placeholder="Search articles..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Category Filter */}
              <div className="flex items-center gap-2">
                <Filter className="text-gray-500 w-5 h-5" aria-hidden="true" />
                <label htmlFor="category" className="sr-only">
                  Select Category
                </label>
                <select
                  id="category"
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as BlogCategory | 'all')}
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="container mx-auto px-4 py-12">
          {filteredPosts.length > 0 ? (
            <>
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {filteredPosts.map((post) => (
                  <BlogCard key={post.slug} post={post} />
                ))}
              </div>

              {/* Results Count */}
              <div className="mt-8 text-center text-gray-600">
                <p>
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'article' : 'articles'} found
                  {searchTerm && ` for "${searchTerm}"`}
                  {selectedCategory !== 'all' && ` in ${selectedCategory}`}
                </p>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg mb-4">
                No articles found
                {searchTerm && ` for "${searchTerm}"`}
                {selectedCategory !== 'all' && ` in ${selectedCategory}`}
              </p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 py-1"
              >
                Reset filters
              </button>
            </div>
          )}
        </section>

        {/* Newsletter CTA */}
        <section className="bg-blue-50 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-4">
                Stay Up to Date
              </h2>
              <p className="text-gray-600 mb-6">
                Get the latest accessibility tips and best practices delivered to your inbox weekly.
              </p>
              <form className="flex gap-4 max-w-md mx-auto">
                <label htmlFor="newsletter-email" className="sr-only">
                  Email address for newsletter
                </label>
                <input
                  id="newsletter-email"
                  type="email"
                  placeholder="Your email address"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
